// src/utils.ts — Helper functions

import { format, differenceInDays, parseISO, addDays, isToday, isBefore, startOfDay } from 'date-fns';
import { Attempt, DayLog, Challenge, MOTIVATIONAL_QUOTES } from './types';

export function todayStr(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function formatDate(dateStr: string): string {
  return format(parseISO(dateStr), 'MMM d, yyyy');
}

export function formatDateShort(dateStr: string): string {
  return format(parseISO(dateStr), 'MMM d');
}

export function formatWeekday(dateStr: string): string {
  return format(parseISO(dateStr), 'EEEE');
}

export function getDayNumber(attempt: Attempt, dateStr?: string): number {
  const target = dateStr || todayStr();
  const diff = differenceInDays(parseISO(target), parseISO(attempt.startDate));
  return diff + 1;
}

export function getDateForDay(attempt: Attempt, dayNum: number): string {
  return format(addDays(parseISO(attempt.startDate), dayNum - 1), 'yyyy-MM-dd');
}

export function isDayComplete(log: DayLog | undefined, tasks: { id: string; required: boolean }[]): boolean {
  if (!log) return false;
  return tasks
    .filter(t => t.required)
    .every(t => log.tasks[t.id] === true);
}

export function getStreak(attempt: Attempt, challenge: Challenge): number {
  let streak = 0;
  const today = todayStr();
  const dayNum = getDayNumber(attempt, today);
  
  for (let day = dayNum; day >= 1; day--) {
    const dateStr = getDateForDay(attempt, day);
    const log = attempt.logs[dateStr];
    
    if (dateStr === today && !isDayComplete(log, challenge.tasks)) {
      continue;
    }
    
    if (isDayComplete(log, challenge.tasks)) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
}

export function getLongestStreak(attempt: Attempt, challenge: Challenge): number {
  let longest = 0;
  let current = 0;
  const dayNum = getDayNumber(attempt);
  
  for (let day = 1; day <= dayNum; day++) {
    const dateStr = getDateForDay(attempt, day);
    const log = attempt.logs[dateStr];
    if (isDayComplete(log, challenge.tasks)) {
      current++;
      longest = Math.max(longest, current);
    } else {
      current = 0;
    }
  }
  
  return longest;
}

export function getTotalCompletedDays(attempt: Attempt, challenge: Challenge): number {
  return Object.values(attempt.logs).filter(log => isDayComplete(log, challenge.tasks)).length;
}

export function getCompletionPercentage(attempt: Attempt, challenge: Challenge): number {
  const completed = getTotalCompletedDays(attempt, challenge);
  return Math.round((completed / challenge.durationDays) * 100);
}

export function isFutureDate(dateStr: string): boolean {
  const today = startOfDay(new Date());
  const date = parseISO(dateStr);
  return isBefore(today, date) && !isToday(date);
}

export function isPastDate(dateStr: string): boolean {
  return isBefore(parseISO(dateStr), startOfDay(new Date())) && !isToday(parseISO(dateStr));
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function getRandomQuote(): string {
  return MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)];
}

export function getTaskCompletionToday(log: DayLog | undefined, tasks: { id: string; required: boolean }[]): { done: number; total: number } {
  const required = tasks.filter(t => t.required);
  const done = required.filter(t => log?.tasks[t.id]).length;
  return { done, total: required.length };
}

export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 6) return 'Late night grind';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Late night grind';
}

export function getMilestone(day: number, total: number): string | null {
  const pct = (day / total) * 100;
  if (day === 1) return "Day 1 — it starts now 🔥";
  if (day === 7) return "One week strong 💪";
  if (day === 14) return "Two weeks in — you're building a habit";
  if (day === 21) return "21 days — they say this is when habits form";
  if (day === 30) return "30 days done — you're not the same person";
  if (pct >= 50 && pct < 52) return "Halfway there 🏔️";
  if (day === 50) return "50 days — most people quit by now. Not you.";
  if (day === 60) return "60 days — the finish line is in sight";
  if (day === total - 7) return "One week to go 🎯";
  if (day === total - 1) return "Tomorrow you finish. Let that sink in.";
  if (day === total) return "YOU DID IT 🏆";
  return null;
}
